import React, { useState, useEffect } from 'react';
import PivotTable from 'react-pivottable/PivotTable';
import 'react-pivottable/pivottable.css';
import { Observer } from 'mobx-react';
import { Formik } from 'formik';
import { css } from '@emotion/core';
import { useRootStore } from '../../../stores/BridgeProvider';
import Spinner from '../../util/Spinner';

const PivotTableContainer = css`
  display: flex;
  flex: 1;
  width: 100%;
  overflow-x: auto;
  overflow-y: auto;
  .pvtOutput {
    width: 100% !important;
  }
`;

const iFilterValues = {
  startDate: '',
  endDate: '',
  transactionCategories: [],
};

const ReportSnapshot = ({ reportId }) => {
  const { dataStore, authStore } = useRootStore();
  const [containerLoading, setContainerLoading] = useState(true);
  const [initialValues, setInitialValues] = useState(iFilterValues);
  const [tableState, setTableState] = useState(null);
  const [reportType, setReportType] = useState('');

  const getReportTemplate = async id => {
    try {
      const { data } = await authStore.request.get(
        `${process.env.REACT_APP_API}/api/report-template/${id}`,
      );
      const { TemplateData, TemplateFilters, Type } = data;
      setInitialValues(TemplateFilters);
      setTableState(TemplateData);
      setReportType(Type);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    const initialize = async () => {
      const requests = [];
      if (reportId) {
        requests.push(getReportTemplate(reportId));
      }
      await Promise.all(requests);
      setContainerLoading(false);
    };
    initialize();
  }, [reportId]);

  if (containerLoading) return <Spinner />;

  return (
    <Formik
      initialValues={initialValues}
      render={({ values }) => (
        <Observer
          render={() => {
            const [isLoading, setLoading] = useState(true);
            const [tableDeals, setTableDeals] = useState([]);
            const transactionCategories =
              dataStore.validTransactionCategoryArray;

            const updateAllData = newData => {
              setTableDeals(newData);
              if (tableState) {
                const tState = { ...tableState };
                tState.data = newData;
                setTableState(tState);
              }
            };

            const getDealReport = async (passedValues = values) => {
              try {
                const { data } = await authStore.request.get(
                  `${process.env.REACT_APP_API}/api/deal-report`,
                  {
                    params: {
                      StartDate: passedValues.startDate || null,
                      EndDate: passedValues.endDate || null,
                      CategoryIds: passedValues.transactionCategories,
                    },
                  },
                );
                updateAllData(data);
              } catch (e) {
                console.error(e);
              }
            };

            const getDealTranReport = async (passedValues = values) => {
              try {
                const { data } = await authStore.request.get(
                  `${process.env.REACT_APP_API}/api/deal-tran-report`,
                  {
                    params: {
                      StartDate: passedValues.startDate || null,
                      EndDate: passedValues.endDate || null,
                    },
                  },
                );
                updateAllData(data);
              } catch (e) {
                console.error(e);
              }
            };

            const getDealTranAccReport = async (passedValues = values) => {
              try {
                const { data } = await authStore.request.get(
                  `${process.env.REACT_APP_API}/api/deal-tran-acc-report`,
                  {
                    params: {
                      StartDate: passedValues.startDate || null,
                      EndDate: passedValues.endDate || null,
                    },
                  },
                );
                updateAllData(data);
              } catch (e) {
                console.error(e);
              }
            };

            const getReportByType = {
              Deal: getDealReport,
              DealTran: getDealTranReport,
              DealTranAcc: getDealTranAccReport,
            };
            const getReport = getReportByType[reportType];

            useEffect(() => {
              const initialize = async () => {
                const requests = [];
                if (transactionCategories.length === 0) {
                  requests.push(dataStore.retrieveTransactionCategories());
                }
                if (getReport) {
                  requests.push(getReport());
                }

                await Promise.all(requests);
                setLoading(false);
              };
              initialize();
            }, []);

            if (isLoading) return <Spinner />;

            return (
              <div css={PivotTableContainer}>
                <PivotTable
                  data={tableDeals}
                  onChange={s => setTableState(s)}
                  unusedOrientationCutoff={Infinity}
                  {...tableState}
                />
              </div>
            );
          }}
        />
      )}
    />
  );
};

export default ReportSnapshot;
